import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";

import { useSelector, useDispatch } from "react-redux";

import {
  getAllAccountsUser,
  getAccountsExhibitor,
  changeAccounts,
} from "../../../redux/modules/account/actions";

import { DialogContext } from "../../../hooks/dialog/context";
import { useLoading } from "../../../hooks/loading";

import { useIntl } from "react-intl";

import { useHistory } from "react-router-dom";

import useNotification from "../../../hooks/newUXNotification";

import SelectUser from "../../SelectUser";
import DropdownFlags from "../../DropdownFlags";

import ModalSettings from "../../../newUXComponents/ModalSettings";
import ConfirmationModal from "../../../components/ConfirmationModal";

import AlterEvent from "../../../newUXAssets/icons/AlterEvent";
import SignOut from "../../../newUXAssets/icons/SignOut";
import LogoutFirstAccessModal from "../../Modal/LogoutFirstAccessModal";

import { Container, ContentLeft, ContainerRight, Icon, Text } from "./styles";

const Header = ({isTransparent}) => {
  const intl = useIntl();
  const { createDialog } = useContext(DialogContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const notification = useNotification();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const {
    user,
    adminToken,
    serviceProviderLoggingAsAnExhibitor,
    keepSignedIn,
    accountCreated,
  } = useSelector((state) => state.auth);
  const { event, contract } = useSelector((state) => state.initialSettings);
  const { accountsExhibitor, accountsServiceProvider } = useSelector(
    (state) => state.account
  );
  const language = useSelector((state) => state.language).code;
  const isAdministrator = useMemo(() =>
    user?.role?.type === "ADMINISTRATOR",
    [user]);
  const isExhibitor = useMemo(() =>
    user?.role?.type === "EXHIBITOR",
    [user]);
  const isSponsor = useMemo(() =>
    user?.role?.type === "SPONSOR_WITHOUT_M2",
    [user]);
  const isDigital = useMemo(() =>
    user?.role?.type === "DIGITAL_ONLY",
    [user]);
  const isServiceProvider = useMemo(() =>
    user?.role?.type === "SERVICE_PROVIDER",
    [user]);

  const { isLoading } = useLoading();

  const handleLogout = () => {
    if (isAdministrator || adminToken) {
      window.location.href =
        process.env.REACT_APP_SERVER_URL + "saml/logout?local=true";
    } else {
      window.location.href = process.env.REACT_APP_LOGOUT_URI;
    }
  };

  const handleModalSettings = () =>
    createDialog({
      id: "new-header-settings",
      Component: ModalSettings,
      props: {},
    });

  const handleModal = () => {
    if (!accountCreated) {
      createDialog({
        id: "logout-account-created",
        Component: LogoutFirstAccessModal,
        props: {
          handleLogout,
        },
      });
    } else {
      setModalIsOpen(true);
    }
  };

  const callBackMessage = useCallback(
    (message) => {
      if (message === -2025) {
        notification.displayInfo(
          intl.formatMessage({
            id: "api.error." + message,
          }),
          "info"
        );
      } else {
        notification.displayError(
          intl.formatMessage({
            id: "api.error." + message,
          }),
          "error"
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl]
  );

  const callbackSuccess = useCallback(() => {
    history.push("/home");
  }, [history]);

  const handleChangeAccount = useCallback(
    (selectedAccount) => {
      dispatch(
        changeAccounts({
          params: {
            accountIdNumber: selectedAccount?.accountId,
            roleType: selectedAccount?.type,
          },
          endpointType:
            !adminToken
            && (serviceProviderLoggingAsAnExhibitor
                || isServiceProvider)
              ? "SERVICE_PROVIDER"
              : "EXHIBITOR",
          keepSignedIn,
          callbackSuccess,
          callBackMessage,
        })
      );
    },
    [
      callBackMessage,
      callbackSuccess,
      keepSignedIn,
      isServiceProvider,
      dispatch,
      serviceProviderLoggingAsAnExhibitor,
      adminToken,
    ]
  );

  const accountsOptions = useMemo(() => {
    if (
      !adminToken &&
      (serviceProviderLoggingAsAnExhibitor ||
        isServiceProvider)
    ) {
      return accountsServiceProvider;
    }

    return accountsExhibitor;
  }, [
    accountsExhibitor,
    accountsServiceProvider,
    adminToken,
    isServiceProvider,
    serviceProviderLoggingAsAnExhibitor,
  ]);

  const nameEvent = useMemo(() => {
    if (event?.label) {
      return event?.label;
    }
    return intl.formatMessage({ id: "event_edition" });
  }, [event, intl]);

  const nameContract = useMemo(() => {
    if (contract?.label) {
      return contract?.label;
    }
    return intl.formatMessage({ id: "contract" });
  }, [contract, intl]);

  const defaultAccountName = useMemo(() => {
    if (!user?.name) {
      return intl.formatMessage({ id: "select_your_profile" })
    }
    if (isExhibitor) {
      return intl.formatMessage(
        { id: "default_account_name_exhibitor"},
        { name: user?.name }
      )
    }
    if (isSponsor) {
      return intl.formatMessage(
        { id: "default_account_name_sponsor_without_m2"},
        { name: user?.name }
      )
    }
    if (isDigital) {
      return intl.formatMessage(
        { id: "default_account_name_digital_only"},
        { name: user?.name }
      )
    }
    if (isServiceProvider) {
      return intl.formatMessage(
        { id: "default_account_name_service_provider"},
        { name: user?.name }
      )
    }
    return user?.name;
  }, [
    user,
    intl,
    isServiceProvider,
    isExhibitor,
    isSponsor,
    isDigital
  ]);

  useEffect(() => {
    if (
      !adminToken &&
      (serviceProviderLoggingAsAnExhibitor ||
        isServiceProvider)
    ) {
      dispatch(getAllAccountsUser({ callBackMessage, intl, language }));
    } else if (!adminToken
        && (isExhibitor || isSponsor || isDigital)) {
      dispatch(getAccountsExhibitor({ callBackMessage, intl, language }));
    }
  }, [
    dispatch,
    callBackMessage,
    adminToken,
    isServiceProvider,
    isExhibitor,
    isSponsor,
    isDigital,
    serviceProviderLoggingAsAnExhibitor,
    intl,
    language,
  ]);

  return (
    <Container $isTransparent={isTransparent}>
      <ContentLeft>
        <Text>
          <span>{nameEvent}</span>
          <span>{nameContract}</span>
        </Text>
        <Icon onClick={() => handleModalSettings()}>
          <AlterEvent />
        </Icon>
      </ContentLeft>
      <ContainerRight>
        {!adminToken && (
          <SelectUser
            name="account"
            options={accountsOptions}
            defaultValue={defaultAccountName}
            placeholder={defaultAccountName}
            onChange={(value, selectedAccount) =>
              handleChangeAccount(selectedAccount)
            }
            $isDisabled={isLoading}
          />
        )}
        <DropdownFlags />
        <Icon onClick={() => handleModal()}>
          <SignOut />
        </Icon>
      </ContainerRight>
      <ConfirmationModal
        setIsOpen={setModalIsOpen}
        modalIsOpen={modalIsOpen}
        confirmationFunction={handleLogout}
        description={intl.formatMessage({ id: "logout_confirmation" })}
        confirmationText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
        isLoading={isLoading}
      />
    </Container>
  );
};

export default Header;
